import { getActors, getFirstAction } from "@mirage/service-feed/util/helpers";
import { Trans } from "@mirage/translations";
import { ActivityLabelLink } from "../ActivityLabel/ActivityLabelLink";
import {
  ActivityLabelSubject,
  ActivityLabelSubjectOverflow,
  getActorNames,
} from "../ActivityLabel/ActivityLabelSubject";

import type { dash_feed, stacks, users } from "@dropbox/api-v2-client";

type StackActivityLabelProps = {
  activity: dash_feed.FeedItem;
  stack: stacks.StackData;
  currentAccount?: users.FullAccount;
};

export const StackActivityLabel = ({
  activity,
  stack,
}: StackActivityLabelProps) => {
  const action = getFirstAction(activity);
  switch (action?.[".tag"]) {
    case "edit":
      return (
        <EditStackActivityAction
          activity={activity}
          stack={stack}
          action={action}
        />
      );
    case "share":
      return <ShareStackActivityAction activity={activity} stack={stack} />;
    case "add_child":
      return (
        <AddStackItemActivityAction
          activity={activity}
          stack={stack}
          action={action}
        />
      );
    case "remove_child":
      return (
        <RemoveStackItemActivityAction
          activity={activity}
          stack={stack}
          action={action}
        />
      );
    default:
      return null;
  }
};

type EditStackActivityActionProps = {
  action: dash_feed.action_unionEdit;
} & StackActivityLabelProps;

export const EditStackActivityAction = ({
  activity,
  stack,
  action,
}: EditStackActivityActionProps) => {
  if (!action.updates || action.updates.length == 0) {
    return null;
  }

  const firstUpdate = action.updates[0].update;
  if (firstUpdate?.[".tag"] == "stack_update") {
    const updatedField = firstUpdate.field;
    switch (updatedField?.[".tag"]) {
      case "name":
        return (
          <EditStackNameActivityAction
            activity={activity}
            stack={stack}
            update={updatedField}
          />
        );
      case "description":
        return (
          <EditStackDescriptionActivityAction
            activity={activity}
            stack={stack}
            update={updatedField}
          />
        );
      case "is_archived":
        return (
          <ArchiveStackActivityAction
            activity={activity}
            stack={stack}
            update={updatedField}
          />
        );
      default:
        return null;
    }
  }

  return null;
};

type EditStackNameActivityActionProps = {
  update: dash_feed.field_unionName;
} & StackActivityLabelProps;

const EditStackNameActivityAction = ({
  activity,
}: EditStackNameActivityActionProps) => {
  const actors = getActors(activity);
  const { actorNamesToDisplay, actorsToDisplay } = getActorNames(actors);

  // John Doe changed the title
  if (actorNamesToDisplay.length === 0) {
    return (
      <Trans
        i18nKey="activity_feed_expanded_label_edit_stack_name_no_actor"
        components={{
          subject: <ActivityLabelSubject />,
        }}
      />
    );
  }

  return (
    <Trans
      i18nKey="activity_feed_expanded_label_edit_stack_name_one_actor"
      values={{
        actorName1: actorNamesToDisplay[0],
      }}
      components={{
        subject: <ActivityLabelSubject actorEmail={actorsToDisplay[0].email} />,
      }}
    />
  );
};

type EditStackDescriptionActivityActionProps = {
  update: dash_feed.field_unionDescription;
} & StackActivityLabelProps;

const EditStackDescriptionActivityAction = ({
  activity,
}: EditStackDescriptionActivityActionProps) => {
  const actors = getActors(activity);
  const { actorNamesToDisplay, actorsToDisplay } = getActorNames(actors);

  // John Doe changed the title
  if (actorNamesToDisplay.length === 0) {
    return (
      <Trans
        i18nKey="activity_feed_expanded_label_edit_stack_description_no_actor"
        components={{
          subject: <ActivityLabelSubject />,
        }}
      />
    );
  }

  return (
    <Trans
      i18nKey="activity_feed_expanded_label_edit_stack_description_one_actor"
      values={{
        actorName1: actorNamesToDisplay[0],
      }}
      components={{
        subject: <ActivityLabelSubject actorEmail={actorsToDisplay[0].email} />,
      }}
    />
  );
};

type ArchiveStackActivityActionProps = {
  update: dash_feed.field_unionIsArchived;
} & StackActivityLabelProps;

const ArchiveStackActivityAction = ({
  activity,
  update,
}: ArchiveStackActivityActionProps) => {
  const actors = getActors(activity);
  const { actorNamesToDisplay, actorsToDisplay } = getActorNames(actors);
  const isArchived = update.new_value;

  // John Doe [un]archived your Project Planning stack
  if (actorNamesToDisplay.length === 0) {
    return (
      <Trans
        i18nKey={
          isArchived
            ? "activity_feed_expanded_label_archive_stack_no_actor"
            : "activity_feed_expanded_label_unarchive_stack_no_actor"
        }
        components={{
          subject: <ActivityLabelSubject />,
        }}
      />
    );
  }

  return (
    <Trans
      i18nKey={
        isArchived
          ? "activity_feed_expanded_label_archive_stack_one_actor"
          : "activity_feed_expanded_label_unarchive_stack_one_actor"
      }
      values={{
        actorName1: actorNamesToDisplay[0],
      }}
      components={{
        subject: <ActivityLabelSubject actorEmail={actorsToDisplay[0].email} />,
      }}
    />
  );
};

type ShareStackActivityActionProps = {} & StackActivityLabelProps;

export const ShareStackActivityAction = ({
  activity,
}: ShareStackActivityActionProps) => {
  const actors = getActors(activity);
  const { actorNamesToDisplay, actorsToDisplay } = getActorNames(actors);

  // John Doe shared Project Planning stack with you
  if (actorNamesToDisplay.length === 0) {
    return (
      <Trans
        i18nKey="activity_feed_expanded_label_share_stack_no_actor"
        components={{
          subject: <ActivityLabelSubject />,
        }}
      />
    );
  }

  return (
    <Trans
      i18nKey="activity_feed_expanded_label_share_stack_one_actor"
      values={{
        actorName1: actorNamesToDisplay[0],
      }}
      components={{
        subject: <ActivityLabelSubject actorEmail={actorsToDisplay[0].email} />,
      }}
    />
  );
};

type AddStackItemActivityActionProps = {
  action: dash_feed.action_unionAddChild;
} & StackActivityLabelProps;

export const AddStackItemActivityAction = ({
  activity,
  action,
}: AddStackItemActivityActionProps) => {
  const actors = getActors(activity);
  const { actorNamesToDisplay, extraActorsToDisplay, actorsToDisplay } =
    getActorNames(actors, 2);
  const addedItem = action.added_object;
  const addedItemName = addedItem?.name;
  const addedItemUrl = addedItem?.url ?? "#";
  const totalFilteredActors =
    actorNamesToDisplay.length + extraActorsToDisplay.length;
  const itemCount = activity.activities?.length ?? 0;

  // John Doe shared Project Planning stack with you
  if (totalFilteredActors === 0) {
    return (
      <Trans
        i18nKey="activity_feed_expanded_label_add_stack_item_no_actor"
        values={{ addedItemName }}
        components={{
          subject: <ActivityLabelSubject />,
          addedItemLink: <ActivityLabelLink href={addedItemUrl} />,
        }}
      />
    );
  }

  if (totalFilteredActors === 1 && itemCount > 1) {
    return (
      <Trans
        i18nKey="activity_feed_expanded_label_add_stack_item_one_actor_multiple_items"
        values={{
          actorName1: actorNamesToDisplay[0],
          itemCount: itemCount,
        }}
        components={{
          subject: (
            <ActivityLabelSubject actorEmail={actorsToDisplay[0].email} />
          ),
        }}
      />
    );
  }

  if (totalFilteredActors === 2) {
    return (
      <Trans
        i18nKey="activity_feed_expanded_label_add_stack_two_actors"
        values={{
          actorName1: actorNamesToDisplay[0],
          actorName2: actorNamesToDisplay[1],
          itemCount: itemCount,
        }}
        components={{
          subject1: (
            <ActivityLabelSubject actorEmail={actorsToDisplay[0].email} />
          ),
          subject2: (
            <ActivityLabelSubject actorEmail={actorsToDisplay[1].email} />
          ),
        }}
      />
    );
  }
  if (totalFilteredActors === 3) {
    return (
      <Trans
        i18nKey="activity_feed_expanded_label_add_stack_three_actors"
        values={{
          actorName1: actorNamesToDisplay[0],
          actorName2: actorNamesToDisplay[1],
          actorCount: extraActorsToDisplay.length,
          itemCount: itemCount,
        }}
        components={{
          subject1: (
            <ActivityLabelSubject actorEmail={actorsToDisplay[0].email} />
          ),
          subject2: (
            <ActivityLabelSubject actorEmail={actorsToDisplay[1].email} />
          ),
          subjectOverflow: (
            <ActivityLabelSubjectOverflow extraActors={extraActorsToDisplay} />
          ),
        }}
      />
    );
  }

  if (totalFilteredActors > 3) {
    return (
      <Trans
        i18nKey="activity_feed_expanded_label_add_stack_many_actors"
        values={{
          actorName1: actorNamesToDisplay[0],
          actorName2: actorNamesToDisplay[1],
          actorCount: extraActorsToDisplay.length,
          itemCount: itemCount,
        }}
        components={{
          subject1: (
            <ActivityLabelSubject actorEmail={actorsToDisplay[0].email} />
          ),
          subject2: (
            <ActivityLabelSubject actorEmail={actorsToDisplay[1].email} />
          ),
          subjectOverflow: (
            <ActivityLabelSubjectOverflow extraActors={extraActorsToDisplay} />
          ),
        }}
      />
    );
  }
  // Fallback case is one actor, one item
  return (
    <Trans
      i18nKey="activity_feed_expanded_label_add_stack_item_one_actor"
      values={{
        actorName1: actorNamesToDisplay[0],
        addedItemName,
      }}
      components={{
        subject: <ActivityLabelSubject actorEmail={actorsToDisplay[0].email} />,
        addedItemLink: <ActivityLabelLink href={addedItemUrl} />,
      }}
    />
  );
};

type RemoveChildActivityActionProps = {
  action: dash_feed.action_unionRemoveChild;
} & StackActivityLabelProps;

export const RemoveStackItemActivityAction = ({
  activity,
  action,
}: RemoveChildActivityActionProps) => {
  const actors = getActors(activity);
  const { actorNamesToDisplay, actorsToDisplay } = getActorNames(actors);
  const removedItem = action.removed_object;
  const removedItemName = removedItem?.name;
  const removedItemUrl = removedItem?.url ?? "#";

  // John Doe removed Klien Project Planning from your Project Planning stack
  if (actorNamesToDisplay.length === 0) {
    return (
      <Trans
        i18nKey="activity_feed_expanded_label_remove_stack_item_no_actor"
        values={{ removedItemName }}
        components={{
          subject: <ActivityLabelSubject />,
          removedItemLink: <ActivityLabelLink href={removedItemUrl!} />,
        }}
      />
    );
  }

  return (
    <Trans
      i18nKey="activity_feed_expanded_label_remove_stack_item_one_actor"
      values={{
        actorName1: actorNamesToDisplay[0],
        removedItemName,
      }}
      components={{
        subject: <ActivityLabelSubject actorEmail={actorsToDisplay[0].email} />,
        removedItemLink: <ActivityLabelLink href={removedItemUrl!} />,
      }}
    />
  );
};
